/**
 * Archivos Adjuntos List Component
 * Displays patient file attachments with actions
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Download,
  FileText,
  Image,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  Paperclip,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useArchivosAdjuntos, useDeleteArchivoAdjunto } from "@/hooks/use-archivos-adjuntos";
import type { ArchivoAdjuntoListItem } from "@/hooks/use-archivos-adjuntos";

import { ArchivoAdjuntoFormDialog } from "./archivo-adjunto-form-dialog";
import { ArchivoAdjuntoViewerDialog } from "./archivo-adjunto-viewer-dialog";

interface ArchivosAdjuntosListProps {
  pacienteId: number;
  pacienteNombre: string;
}

export function ArchivosAdjuntosList({ pacienteId, pacienteNombre }: ArchivosAdjuntosListProps) {
  const { t } = useTranslation();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [viewingArchivo, setViewingArchivo] = useState<ArchivoAdjuntoListItem | null>(null);
  const [deletingArchivo, setDeletingArchivo] = useState<ArchivoAdjuntoListItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useArchivosAdjuntos({
    paciente: pacienteId,
    is_active: true,
    ordering: "-fecha_subida",
  });

  const deleteMutation = useDeleteArchivoAdjunto();

  const handleDelete = async () => {
    if (!deletingArchivo) return;

    try {
      await deleteMutation.mutateAsync(deletingArchivo.id);
      toast.success(t("archivosAdjuntos.toast.deleted"));
    } catch {
      setError(t("api.unknownError"));
    } finally {
      setDeletingArchivo(null);
    }
  };

  const handleDownload = (archivo: ArchivoAdjuntoListItem) => {
    if (archivo.url) {
      window.open(archivo.url, "_blank");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-HN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const archivos = data?.results || [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="text-lg">{t("archivosAdjuntos.title")}</CardTitle>
          <CardDescription>{t("archivosAdjuntos.description")}</CardDescription>
        </div>
        <Button onClick={() => setIsFormOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          {t("archivosAdjuntos.new")}
        </Button>
      </CardHeader>

      <CardContent>
        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive mb-4 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : archivos.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Paperclip className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>{t("archivosAdjuntos.noArchivos")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {archivos.map((archivo) => (
              <div
                key={archivo.id}
                className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => setViewingArchivo(archivo)}
              >
                {/* Thumbnail or Icon */}
                <div className="flex-shrink-0">
                  {archivo.es_imagen && archivo.url ? (
                    <img
                      src={archivo.url}
                      alt={archivo.nombre_archivo}
                      className="h-12 w-12 rounded object-cover border"
                      onError={(e) => {
                        // Fallback to icon if image fails to load
                        e.currentTarget.style.display = "none";
                        e.currentTarget.nextElementSibling?.classList.remove("hidden");
                      }}
                    />
                  ) : null}
                  <div className={archivo.es_imagen && archivo.url ? "hidden" : ""}>
                    {archivo.es_imagen ? (
                      <Image className="h-8 w-8 text-blue-500" />
                    ) : archivo.es_pdf ? (
                      <FileText className="h-8 w-8 text-red-500" />
                    ) : (
                      <FileText className="h-8 w-8 text-gray-500" />
                    )}
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{archivo.nombre_archivo}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Badge variant="outline" className="text-xs">
                      {archivo.tipo_display}
                    </Badge>
                    <span>{archivo.tamaño_legible}</span>
                    <span className="hidden sm:inline">•</span>
                    <span className="hidden sm:inline">{formatDate(archivo.fecha_subida)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div
                  className="flex items-center gap-1 flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDownload(archivo)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t("archivosAdjuntos.download")}</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setDeletingArchivo(archivo)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t("common.delete", "Eliminar")}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Form Dialog */}
      <ArchivoAdjuntoFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        pacienteId={pacienteId}
        pacienteNombre={pacienteNombre}
      />

      {/* Viewer Dialog */}
      {viewingArchivo && (
        <ArchivoAdjuntoViewerDialog
          open={!!viewingArchivo}
          onOpenChange={(open) => !open && setViewingArchivo(null)}
          archivoId={viewingArchivo.id}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deletingArchivo}
        onOpenChange={(open) => !open && setDeletingArchivo(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("archivosAdjuntos.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("archivosAdjuntos.deleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
